import React from 'react';

import {
  canManageContent,
  canManageUsers,
  type AdminRole,
} from '#shared/adminPermissions';
import {
  isAdminConfigured,
  loadRuntimeConfig,
  type RuntimeConfig,
} from '#src/config';
import {
  getAccessToken,
  getAdminEmail,
  getAdminRole,
  getAdminSubject,
  refreshSessionIfNeeded,
  setCognitoConfig,
} from '#src/utils/cognitoAuth';
import {
  hasLocalAdminSession,
  LOCAL_ADMIN_EMAIL,
  LOCAL_ADMIN_ROLE,
} from '#src/utils/localAdmin';
import { isLocalDemoMode } from '#src/utils/localDemo';

export type AdminPermissions = {
  role: AdminRole | null;
  canManageContent: boolean;
  canManageUsers: boolean;
  email: string | null;
  subject: string | null;
};

type AdminSession =
  | { status: 'loading' }
  | { status: 'unconfigured' }
  | {
      status: 'unauthenticated';
      recheckAuth: () => Promise<void>;
    }
  | {
      status: 'authenticated';
      runtimeConfig: RuntimeConfig;
      permissions: AdminPermissions;
      recheckAuth: () => Promise<void>;
    };

export const useAdminSession = (): AdminSession => {
  const [runtimeConfig, setRuntimeConfig] =
    React.useState<RuntimeConfig | null>(null);
  const [authenticated, setAuthenticated] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const localDemo = isLocalDemoMode();

  const recheckAuth = React.useCallback(async () => {
    if (isLocalDemoMode()) {
      setAuthenticated(hasLocalAdminSession());
      return;
    }
    await refreshSessionIfNeeded();
    setAuthenticated(Boolean(getAccessToken()));
  }, []);

  React.useEffect(() => {
    loadRuntimeConfig().then(async (cfg) => {
      setRuntimeConfig(cfg);

      if (isLocalDemoMode()) {
        setAuthenticated(hasLocalAdminSession());
        setLoading(false);
        return;
      }

      if (!isAdminConfigured(cfg)) {
        setLoading(false);
        return;
      }

      setCognitoConfig(cfg);
      await recheckAuth();
      setLoading(false);
    });
  }, [recheckAuth]);

  if (loading) {
    return { status: 'loading' };
  }

  if (localDemo) {
    const localConfig = runtimeConfig ?? {
      cognitoUserPoolId: '',
      cognitoClientId: '',
      cognitoRegion: 'ap-southeast-2',
      apiUrl: 'http://localhost',
    };

    if (!authenticated) {
      return { status: 'unauthenticated', recheckAuth };
    }

    return {
      status: 'authenticated',
      runtimeConfig: localConfig,
      permissions: {
        role: LOCAL_ADMIN_ROLE,
        canManageContent: canManageContent(LOCAL_ADMIN_ROLE),
        canManageUsers: canManageUsers(LOCAL_ADMIN_ROLE),
        email: LOCAL_ADMIN_EMAIL,
        subject: 'local-admin',
      },
      recheckAuth,
    };
  }

  if (!runtimeConfig || !isAdminConfigured(runtimeConfig)) {
    return { status: 'unconfigured' };
  }

  if (!authenticated) {
    return { status: 'unauthenticated', recheckAuth };
  }

  const role = getAdminRole();
  return {
    status: 'authenticated',
    runtimeConfig,
    permissions: {
      role,
      canManageContent: canManageContent(role),
      canManageUsers: canManageUsers(role),
      email: getAdminEmail(),
      subject: getAdminSubject(),
    },
    recheckAuth,
  };
};
